import { canReachBackendApi, resolveApiRequestUrl } from './apiRequestUrl';

/**
 * @returns {{ message: string, code?: string }}
 */
async function friendlyErrorFromResponse(res) {
  let body = null;
  const ct = res.headers.get('Content-Type') || '';
  if (ct.includes('application/json')) {
    try {
      body = await res.json();
    } catch {
      /* ignore parse errors */
    }
  }
  const serverMsg = typeof body?.error === 'string' ? body.error : null;
  switch (res.status) {
    case 401:
      return { message: 'Your session could not be verified. Please sign out and sign in again.' };
    case 402:
      if (serverMsg === 'INSUFFICIENT_CREDITS') {
        return {
          message: 'You do not have enough credits. Please buy a credit pack to continue.',
          code: 'INSUFFICIENT_CREDITS',
        };
      }
      return { message: serverMsg || 'Payment required.' };
    case 400:
      return { message: serverMsg || 'The request was invalid. Please try again.' };
    case 502:
      return { message: serverMsg || 'Image generation failed. Please try again.' };
    default:
      return { message: serverMsg || 'Something went wrong. Please try again.' };
  }
}

/**
 * @param {import('firebase/auth').User} user
 * @param {{
 *   blob: Blob;
 *   quality: 'standard' | 'hq';
 *   outputType?: string;
 *   cameraView?: string;
 *   furnishingMode?: string;
 *   style?: string;
 *   userNotes?: string;
 *   projectId?: string;
 *   preparedMeta?: {
 *     preparedWidth: number;
 *     preparedHeight: number;
 *     paddingRatio: number;
 *     paddingApplied: boolean;
 *     frontendPrepared: boolean;
 *   };
 *   rawSourceFile?: File | null;
 * }} params
 * @returns {Promise<
 *   | {
 *       ok: true;
 *       imageDataUrl: string;
 *       contentType: string;
 *       model: string;
 *       quality: string;
 *       creditCost: number;
 *       freeTrialApplied: boolean;
 *       creditsRemaining: number | null;
 *       promptUsed: string;
 *       generatedImageUrl?: string;
 *       project?: Record<string, unknown>;
 *     }
 *   | { ok: false; error: string; code?: string }
 * >}
 */
export async function requestFloorPlanTo3DImage(user, {
  blob,
  quality,
  outputType,
  cameraView,
  furnishingMode,
  style,
  userNotes,
  projectId,
  preparedMeta,
  rawSourceFile,
}) {
  if (!user) {
    return { ok: false, error: 'You must be signed in to generate a 3D floor plan image.' };
  }
  if (!canReachBackendApi()) {
    return {
      ok: false,
      error: 'The app is not configured to reach the design server. Please contact support.',
    };
  }
  if (!blob || !(blob instanceof Blob)) {
    return { ok: false, error: 'Prepared image is missing. Upload and prepare a floor plan first.' };
  }
  if (quality !== 'standard' && quality !== 'hq') {
    return { ok: false, error: 'Please select a quality option (Standard 2K or HQ 4K).' };
  }

  try {
    const idToken = await user.getIdToken(true);
    const formData = new FormData();
    formData.append('image', blob, 'floorplan-prepared.jpg');
    formData.append('quality', quality);
    formData.append('frontendPrepared', 'true');

    if (preparedMeta) {
      formData.append('preparedWidth', String(preparedMeta.preparedWidth));
      formData.append('preparedHeight', String(preparedMeta.preparedHeight));
      formData.append('paddingRatio', String(preparedMeta.paddingRatio));
      if (preparedMeta.paddingApplied) {
        formData.append('paddingApplied', 'true');
      }
    }

    if (outputType) {
      formData.append('outputType', outputType);
    }
    if (typeof cameraView === 'string' && cameraView.trim()) {
      formData.append('cameraView', cameraView.trim());
    }
    if (typeof furnishingMode === 'string' && furnishingMode.trim()) {
      formData.append('furnishingMode', furnishingMode.trim());
    }
    if (typeof style === 'string' && style.trim()) {
      formData.append('style', style.trim());
    }
    if (typeof userNotes === 'string' && userNotes.trim()) {
      formData.append('userNotes', userNotes.trim());
    }
    if (typeof projectId === 'string' && projectId.trim()) {
      formData.append('projectId', projectId.trim());
    }
    if (rawSourceFile instanceof File && rawSourceFile.size > 0) {
      formData.append('rawImage', rawSourceFile, rawSourceFile.name || 'floorplan-raw.jpg');
    }

    const res = await fetch(resolveApiRequestUrl('/api/test/floorplan-to-3d-image'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
      body: formData,
    });

    if (res.ok) {
      let body = null;
      try {
        body = await res.json();
      } catch {
        /* ignore */
      }
      const imageBase64 = typeof body?.imageBase64 === 'string' ? body.imageBase64 : '';
      const contentType =
        typeof body?.contentType === 'string' ? body.contentType : 'image/jpeg';
      if (!imageBase64) {
        return { ok: false, error: 'The server did not return an image.' };
      }
      const project =
        body?.project && typeof body.project === 'object' ? body.project : undefined;
      return {
        ok: true,
        imageDataUrl: `data:${contentType};base64,${imageBase64}`,
        contentType,
        model: typeof body?.model === 'string' ? body.model : '',
        quality: typeof body?.quality === 'string' ? body.quality : quality,
        creditCost: typeof body?.creditCost === 'number' ? body.creditCost : 0,
        freeTrialApplied: body?.freeTrialApplied === true,
        creditsRemaining:
          typeof body?.creditsRemaining === 'number' ? body.creditsRemaining : null,
        generatedImageUrl:
          typeof body?.generatedImageUrl === 'string' ? body.generatedImageUrl : '',
        ...(project ? { project } : {}),
        promptUsed: typeof body?.promptUsed === 'string' ? body.promptUsed : '',
      };
    }

    const { message, code } = await friendlyErrorFromResponse(res);
    return { ok: false, error: message, code };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err ?? '');
    const likelyCors =
      typeof message === 'string' &&
      (message.includes('Failed to fetch') || message.includes('NetworkError'));
    return {
      ok: false,
      error:
        process.env.NODE_ENV === 'development' && message
          ? `Could not reach the server. (${message})`
          : likelyCors
            ? 'Could not reach the server. Check your connection and try again.'
            : 'Could not reach the server. Check your connection and try again.',
    };
  }
}
